import { api } from '@moveup/app-core';

const initialState = {
	contexts: [],
	selectedContext: null,
	contextsLoading: false,
};

const store = {
	state: initialState,
	mutations: {
		setContexts(state, contexts) { state.contexts = contexts; },
		setSelectedContext(state, context) { state.selectedContext = context; },
		setContextsLoading(state, loading) { state.contextsLoading = loading; },
		setUser(state, user) { state.user = user; },
		setApplication(state, application) { state.application = application; },
	},
	actions: {
		patchUser({ commit }, { id, ...params }) {
			return api.patch(`users/${id}`, params).then(result => {
				if (result.value) commit('setUser', result.value);
				return result;
			});
		},

		getUsers(_, params) {
			return api.get('users', params);
		},

		getContextUsers(_, { contextId, params }) {
			return api.get(`contexts/${contextId}/users`, params);
		},

		getUser(_, id) {
			return api.get(`users/${id}`);
		},

		deleteUsers(_, params) {
			return api.del('users', params);
		},

		createUser(_, params) {
			return api.post('users', params);
		},

		updateUser(_, { _id: id, ...params }) {
			return api.patch(`users/${id}`, params);
		},

		patchApplication({ commit }, { id, ...params }) {
			return api.patch(`applications/${id}`, params).then(result => {
				if (result.value) commit('setApplication', result.value);
				return result;
			});
		},

		getApplications(_, params) {
			return api.get('applications', params);
		},
		getTags(_) {
			return api.get('tags')
		},
		searchLinkedPatients(_, params) {
			return api.get('users/patients', params)
		},
		getApplication(_, id) {
			return api.get(`applications/${id}`);
		},
		getPlatformFlavor(_, id) {
			return api.get(`platformflavors/${id}`)
		},

		deleteApplications(_, params) {
			return api.del('applications', params);
		},

		createApplication(_, params) {
			return api.post('applications', params);
		},

		updateApplication(_, { _id, ...params }) {
			return api.patch(`applications/${_id}`, params);
		},
		updatePlatformFlavor(_, { _id, ...params }) {
			return api.patch(`platformflavors/${_id}`, params);
		},

		getScopes(_, params) {
			return api.get('scopes', params);
		},
		getContexts(_, params) {
			return api.get('contexts', params);
		},

		async loadContexts({ commit, dispatch }) {
			commit('setContextsLoading', true);
			try {
				const data = await dispatch('getContexts');
				// Defensive: handle both { results: [...] } and flat array responses
				const results = data?.results ?? (Array.isArray(data) ? data : []);
				commit('setContexts', results);

				const storedId = localStorage.getItem('auth-admin:selectedContextId');
				const stored = storedId ? results.find(c => c._id === storedId) : null;
				const selected = stored ?? results[0] ?? null;

				commit('setSelectedContext', selected);
				if (selected) {
					localStorage.setItem('auth-admin:selectedContextId', selected._id);
				}
			} catch (err) {
				commit('setContexts', []);
				commit('setSelectedContext', null);
				throw err;
			} finally {
				commit('setContextsLoading', false);
			}
		},

		setSelectedContext({ commit }, context) {
			commit('setSelectedContext', context);
			if (context) {
				localStorage.setItem('auth-admin:selectedContextId', context._id);
			}
		},
		addUserToContext(_, { contextId, userId, contextRole, permissions = [] }) {
			return api.put(`contexts/${contextId}/users/${userId}`, { contextRole, permissions });
		},
		getOrganizations(_, params) {
			return api.get('organizations', params);
		},
		getLanguages(_, params) {
			return api.get('languages', params);
		},
		getCodes(_, params) {
			return api.get('codes', params);
		},
		getCode(_, id) {
			return api.get(`codes/${id}`);
		},
		deleteCodes(_, params) {
			return api.del('codes', params);
		},
		createCode(_, params) {
			return api.post('codes', params);
		},
		updateCode(_, { _id: id, ...params }) {
			return api.patch(`codes/${id}`, params);
		},
		getRoles(_, params) {
			return api.get('roles', params);
		},
		getDisciplines(_, params) {
			return api.get('disciplines', params);
		},
		resetPassword(_, { _id: id, ...params }) {
			return api.post(`reset/${id}`, params);
		},
		linkPatientWithUser(_, {_id: id, ...params}) {
			return api.post(`user/patient/${id}`, params)
		},
		activate(_, params){
			return api.post(`activate`, params);
		},
		getPlatformFlavors(_, params) {
			return api.get('platformflavors', params);
		},
		getAllStudies(_, params) {
			return api.get('studies', params);
		},
		getEmailTemplates(_, params) {
			return api.get('emailtemplates', params);
		},
		getUserLogs(_, {userId: userId, ...params}) {
			return api.get(`users/${userId}/logs`, params);
		},
		getTemplates(_, params) {
			return api.get('usertemplates', params);
		},
		getTemplate(_, id) {
			return api.get(`usertemplates/${id}`);
		},
		deleteTemplates(_, params) {
			return api.del('usertemplates', params);
		},
		createTemplate(_, params) {
			return api.post('usertemplates', params);
		},
		updateTemplate(_, { _id: id, ...params }) {
			return api.patch(`usertemplates/${id}`, params);
		},
	},
};

export default store;
